import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ContentDialog from '../../../../components/Dialogs/ContentDialog'
import AlertDefault from '../../../../components/Alerts'
import NumberFunctions from '../../../../core/functions/NumberFunctions'
import Convertions from '../../../../core/functions/convertions'
import { dateTimeString } from '../../../../core/functions/dateTime'
import { useAnticipationReconciliation } from '../../../../core/hooks/useAnticipationReconciliations'
const money = NumberFunctions.formatMoneyDefault

export const closedByLabel = (value?: string | null) =>
  value === 'Settled'
    ? 'Taxa confirmada'
    : value === 'WindowEnd'
      ? 'Fim da janela de 31 dias'
      : ''

function Info({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {children || '—'}
      </Typography>
    </Box>
  )
}

interface IAnticipationReconciliationDetailsProps {
  orderChargeId?: string
  onClose: () => void
}

export default function AnticipationReconciliationDetails({
  orderChargeId,
  onClose,
}: IAnticipationReconciliationDetailsProps) {
  const { loading, data, error } = useAnticipationReconciliation(orderChargeId)

  return (
    <ContentDialog
      open={!!orderChargeId}
      onClose={onClose}
      title="Reconciliação de antecipação"
      maxWidth="lg"
      hideButtons
    >
      {loading && (
        <Stack alignItems="center" sx={{ p: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && error !== '' && (
        <AlertDefault severity="warning">{error}</AlertDefault>
      )}

      {!loading && data && (
        <Stack spacing={3}>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            <Info label="Pedido">{data.orderCode}</Info>
            <Info label="Data da venda">
              {dateTimeString(data.saleDate as unknown as Date)}
            </Info>
            <Info label="Cliente">
              {data.customerName}
              {data.customerEmail && (
                <Typography variant="caption" display="block">
                  {data.customerEmail}
                </Typography>
              )}
            </Info>
            <Info label="Produto / Oferta">
              {[data.productName, data.offerName].filter(Boolean).join(' • ')}
            </Info>
            <Info label="Valor">{money(data.transactionValue)}</Info>
            <Info label="Forma de pagamento">
              {Convertions.paymentMethodToString(data.paymentMethod ?? '')}
            </Info>
          </Stack>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Split
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Recebedor</TableCell>
                    <TableCell align="right">Valor antes</TableCell>
                    <TableCell align="right">Valor depois</TableCell>
                    <TableCell align="right">Taxa estimada</TableCell>
                    <TableCell align="right">Taxa real</TableCell>
                    <TableCell align="right">Diferença</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.split.map((item) => {
                    const r = item.reconciliation
                    return (
                      <TableRow key={item.orderValueId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.tenantName ?? item.recipientId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.valueTypeDescription}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {r ? money(r.previousValue) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {r ? money(r.newValue) : money(item.currentValue)}
                        </TableCell>
                        <TableCell align="right">
                          {r ? money(r.estimatedFee) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {r ? money(r.realFee) : '—'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: r?.adjusted ? 'error.main' : undefined,
                            fontWeight: r?.adjusted ? 600 : undefined,
                          }}
                        >
                          {r ? money(r.difference) : '—'}
                        </TableCell>
                        <TableCell>
                          {r && (
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                size="small"
                                label={r.adjusted ? 'Ajustado' : 'Sem ajuste'}
                                color={r.adjusted ? 'error' : 'default'}
                                variant={r.adjusted ? 'filled' : 'outlined'}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {closedByLabel(r.closedBy)} ·{' '}
                                {dateTimeString(r.checkedAt as unknown as Date)}
                              </Typography>
                              {r.notApplied > 0 && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                >
                                  {money(r.notApplied)} não puderam ser
                                  descontados
                                </Typography>
                              )}
                            </Stack>
                          )}
                          {!r && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Fora da reconciliação
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      )}
    </ContentDialog>
  )
}
